import React, {
    ReactElement,
    ReactNode,
    RefObject,
    createContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { batch } from "react-redux";

import { ContainedButton, TextButton } from "common/DS/Buttons";
import { useHasPermanentScrollbar, useValueByBreakpoints } from "lib/hooks/window";
import Button from "@mui/material/Button";
import classNames from "classnames";
import { FilterListIcon } from "common/DS/Icons";
import Dialog from "common/DS/Dialogs";
import { STANDARD_BREAKPOINTS } from "lib/constants";

export const FilterGroupContext = createContext({});
export const FilterGroupProvider = FilterGroupContext.Provider;

type FilterDialogProps = {
    buttons: ReactElement | ReactElement[] | null;
    children: ReactNode;
    filterCount: number;
    handleClear: () => void;
};

function FilterDialog({ buttons = null, children, filterCount, handleClear }: FilterDialogProps) {
    const [showModal, setShowModal] = useState(false);
    const hasScrollbar = useHasPermanentScrollbar();

    const buttonClassName = classNames("ws-filter-button__button", {
        "ws-filter-button__button--active": filterCount > 0,
        "ws-filter-button__button--start-icon": true,
    });

    const onShowModal = () => setShowModal(true);
    const onHideModal = () => setShowModal(false);
    const onClear = () => {
        handleClear();
        onHideModal();
    };

    return (
        <>
            <div className="ws-filter-toolbar">
                <div className="ws-filter-toolbar__filters">
                    <div className="ws-filter-button dib prel">
                        <Button
                            className={buttonClassName}
                            size="small"
                            onClick={onShowModal}
                            startIcon={<FilterListIcon />}
                        >
                            <span className="ws-filter-button__label">Filters</span>
                            {filterCount > 0 && (
                                <span className="ws-filter-button__count">({filterCount})</span>
                            )}
                        </Button>
                    </div>
                </div>
                {buttons}
            </div>
            <Dialog
                disableScrollLock={hasScrollbar}
                open={showModal}
                handleHide={onHideModal}
                title="Filters"
                PaperProps={{ className: "ws-filter-dialog" }}
                actionsLeft={[<TextButton onClick={onClear}>Clear</TextButton>]}
                actionsRight={[
                    // <OutlinedButton onClick={onHideModal}>Cancel</OutlinedButton>,
                    <ContainedButton onClick={onHideModal}>Apply</ContainedButton>,
                ]}
            >
                {children}
            </Dialog>
        </>
    );
}

type FilterToolbarProps = {
    buttons?: ReactElement[];
    children: ReactNode;
    filterCount: number;
};

type Handler = () => void;

function FilterToolbar({ buttons = [], children, filterCount = 0 }: FilterToolbarProps) {
    const handlers: RefObject<{
        [key: string]: Handler;
    }> = useRef({});

    // For now, show the small filter button on a few more breakpoints
    const isXS = useValueByBreakpoints({
        [STANDARD_BREAKPOINTS.xs]: true,
        [STANDARD_BREAKPOINTS.sm]: true,
    });
    const inDialog = isXS;

    // Only show clear button when 2 or more filters are selected
    const showClear = filterCount > 1;

    const registerClearHandler = (name: string, handler: Handler, remove: boolean) => {
        if (remove && handlers.current) {
            delete handlers.current[name];
        } else if (handlers.current) {
            handlers.current[name] = handler;
        }
    };

    const onClear = () => {
        batch(() => {
            Object.values(handlers.current || {}).forEach((handler) => handler());
        });
    };

    const extraButtons = buttons && buttons.length > 0 && (
        <div className="ws-filter-toolbar__buttons">
            {buttons.filter(Boolean).map((button, idx) => React.cloneElement(button, { key: idx }))}
        </div>
    );

    const toolbar = (
        <div className={classNames("ws-filter-toolbar", { "ws-filter-toolbar--dialog": inDialog })}>
            <div className="ws-filter-toolbar__filters">
                {children}
                {showClear && !inDialog && <TextButton onClick={onClear}>Clear</TextButton>}
            </div>
            {!inDialog && extraButtons}
        </div>
    );

    const context = useMemo(
        () => ({
            registerClearHandler,
            inDialog,
        }),
        [isXS],
    );

    return (
        <FilterGroupProvider value={context}>
            {isXS && (
                <FilterDialog
                    filterCount={filterCount}
                    handleClear={onClear}
                    buttons={extraButtons || null}
                >
                    {toolbar}
                </FilterDialog>
            )}
            {!isXS && toolbar}
        </FilterGroupProvider>
    );
}

export default React.memo(FilterToolbar);
